import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

type Options<TValue, TSource = TValue | null | undefined> = {
  value: TSource;
  required?: boolean;
  onSave?: (value: TValue) => void | Promise<void>;
  isEmpty: (value: TValue) => boolean;
  getInitialValue: (value: TSource) => TValue;
};

export const useDataTableEditableColumn = <TValue, TSource = TValue | null | undefined>({
  value,
  required = false,
  onSave,
  isEmpty,
  getInitialValue,
}: Options<TValue, TSource>) => {
  const { formatMessage } = useIntl();
  const normalizedValue = getInitialValue(value);
  const [inputValue, setInputValue] = useState(normalizedValue);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    setInputValue(getInitialValue(value));
    setErrorMessage(undefined);
  }, [value]);

  const handleSave = async (nextValue: TValue = inputValue) => {
    if (!onSave || loading || nextValue === normalizedValue) {
      return;
    }

    if (required && isEmpty(nextValue)) {
      setErrorMessage(formatMessage({ id: "form.help.required.field.generic" }));
      return;
    }

    setInputValue(nextValue);
    setLoading(true);
    try {
      await onSave(nextValue);
      setErrorMessage(undefined);
    } finally {
      setLoading(false);
    }
  };

  const clearErrorMessage = () => setErrorMessage(undefined);

  return {
    inputValue,
    setInputValue,
    loading,
    errorMessage,
    clearErrorMessage,
    handleSave,
  };
};
